<style scoped>
.dialog-card {
  background: white 0% 0% no-repeat padding-box;
}

.dialog-card--large {
  box-shadow: 8px 8px 23px #b9b9b9, -8px -8px 23px #ffffff !important;
}

.dialog-btn {
  background: white 0% 0% no-repeat padding-box;
}

.dialog-btn--large {
  box-shadow: 4px 4px 10px #838383, -4px -4px 10px #ffffff !important;
}

.dialog-btn.v-btn--disabled {
  background-color: #e0e0e0 !important;
  color: rgba(0, 0, 0, 0.38) !important;
  box-shadow: none !important;
}

.dialog-header {
  background-color: #1b2643;
  border-radius: 3px;
  height: 60px;
  color: white;
  font-size: 14px;
}

.dialog-input {
  font-size: 14px;
}

.username-display {
  padding: 10px 12px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.username-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.username-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 4px;
}
</style>

<template>
  <div>
    <v-dialog v-model="dialogVisible" width="500px" persistent>
      <v-card class="mx-auto dialog-card dialog-card--large">
        <v-card-title class="justify-center dialog-header">
          {{ $t('viewCouponLists.resetPasswordDialog.title') }}
        </v-card-title>
        <v-card-text class="pt-6 px-8">
          <!-- Username (read-only) -->
          <div class="mb-5">
            <div class="username-label">
              {{ $t('viewCouponLists.resetPasswordDialog.username') }}
            </div>
            <div class="username-display">
              {{ username }}
            </div>
            <div class="username-hint">
              {{ $t('viewCouponLists.resetPasswordDialog.readOnly') }}
            </div>
          </div>

          <!-- New Password -->
          <!--
            "input" event exposed by vuetify provides the value but we need access to the DOM event.
            We also need to use the capture phase to overwrite the input value
            before the library's handler reads it and assigns to the model
          -->
          <v-text-field
            class="dialog-input"
            v-model="newPassword"
            @input.native.capture="normalizeCredentialInput"
            @compositionend="normalizeCredentialCompositionEnd"
            :label="$t('viewCouponLists.resetPasswordDialog.newPassword')"
            :placeholder="
              $t('viewCouponLists.resetPasswordDialog.newPasswordPlaceholder')
            "
            persistent-placeholder
            outlined
            :type="showNewPassword ? 'text' : 'password'"
            :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showNewPassword = !showNewPassword"
            :error-messages="newPasswordErrors"
          ></v-text-field>

          <!-- Confirm Password -->
          <v-text-field
            class="dialog-input mt-1"
            v-model="confirmPassword"
            @input.native.capture="normalizeCredentialInput"
            @compositionend="normalizeCredentialCompositionEnd"
            :label="$t('viewCouponLists.resetPasswordDialog.confirmPassword')"
            :placeholder="
              $t(
                'viewCouponLists.resetPasswordDialog.confirmPasswordPlaceholder'
              )
            "
            persistent-placeholder
            outlined
            :type="showConfirmPassword ? 'text' : 'password'"
            :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
            @click:append="showConfirmPassword = !showConfirmPassword"
            :error-messages="confirmPasswordErrors"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="justify-end pb-6 px-8">
          <v-btn
            class="dialog-btn dialog-btn--large"
            color="#1b2643"
            dark
            :disabled="isLoading"
            @click="handleCancel"
          >
            {{ $t('viewCouponLists.resetPasswordDialog.cancel') }}
          </v-btn>
          <v-btn
            class="dialog-btn dialog-btn--large white--text ml-3"
            color="#1b2643"
            :loading="isLoading"
            @click="handleAccept"
          >
            {{ $t('viewCouponLists.resetPasswordDialog.accept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for API errors -->
    <snack-bar
      :snackbarAction="showSnackbar"
      :snackbarText="
        String($t('viewCouponLists.resetPasswordDialog.error.genericError'))
      "
      snackbarColor="pink"
      @closeSnackbar="showSnackbar = false"
    />
  </div>
</template>

<script lang="ts">
import {
  normalizeLearnerCredential,
  validateLearnerPassword,
} from "@equ/shared";
import Vue from "vue";
import { packageCouponService } from "@/api/packageCouponService";
import SnackBar from "@/components/Dashboard/PageViewItems/SnackbarComponent.vue";

export default Vue.extend({
  name: "ResetPasswordDialog",

  components: {
    SnackBar,
  },

  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    username: {
      type: String,
      required: true,
    },
    couponCode: {
      type: String,
      required: true,
    },
    packageId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      newPassword: "",
      confirmPassword: "",
      showNewPassword: false,
      showConfirmPassword: false,
      newPasswordErrors: [] as string[],
      confirmPasswordErrors: [] as string[],
      isLoading: false,
      showSnackbar: false,
    };
  },
  computed: {
    dialogVisible: {
      get(): boolean {
        return this.isVisible;
      },
      set(value: boolean) {
        this.$emit("update:isVisible", value);
      },
    },
  },

  methods: {
    normalizeCredentialInput(event: InputEvent) {
      // Changing the value mid-composition makes the Windows IME duplicate text,
      // so composed text is normalized on compositionend instead
      if (event.isComposing) return;

      const input = event.target as HTMLInputElement;
      input.value = normalizeLearnerCredential(input.value);
    },

    normalizeCredentialCompositionEnd(event: CompositionEvent) {
      const input = event.target as HTMLInputElement;
      const normalized = normalizeLearnerCredential(input.value);
      console.log("compositionend", input.value, normalized);
      if (normalized === input.value) return;

      input.value = normalized;
      // Setting the value doesn't fire input, which v-text-field needs to update v-model
      input.dispatchEvent(new Event("input", { bubbles: true }));
    },

    validate(): boolean {
      this.newPasswordErrors = [];
      this.confirmPasswordErrors = [];

      const validationError = validateLearnerPassword(this.newPassword);

      if (!this.newPassword) {
        this.newPasswordErrors.push(
          String(
            this.$t(
              "viewCouponLists.resetPasswordDialog.validation.newPasswordRequired",
            ),
          ),
        );
      } else if (validationError === "tooShort") {
        this.newPasswordErrors.push(
          String(
            this.$t(
              "viewCouponLists.resetPasswordDialog.validation.newPasswordMinLength",
            ),
          ),
        );
      }

      if (validationError === "invalidString") {
        this.newPasswordErrors.push(
          String(
            this.$t(
              "viewCouponLists.resetPasswordDialog.validation.newPasswordInvalidChars",
            ),
          ),
        );
      }

      if (!this.confirmPassword) {
        this.confirmPasswordErrors.push(
          String(
            this.$t(
              "viewCouponLists.resetPasswordDialog.validation.confirmPasswordRequired",
            ),
          ),
        );
      } else if (this.newPassword !== this.confirmPassword) {
        this.confirmPasswordErrors.push(
          String(
            this.$t(
              "viewCouponLists.resetPasswordDialog.validation.passwordsMismatch",
            ),
          ),
        );
      }

      return (
        this.newPasswordErrors.length === 0 &&
        this.confirmPasswordErrors.length === 0
      );
    },

    async handleAccept() {
      if (!this.validate()) return;

      this.isLoading = true;
      const success = await packageCouponService.resetLearnerPassword(
        this.packageId,
        this.couponCode,
        this.newPassword,
      );
      if (success) {
        this.$emit("success");
      } else {
        this.showSnackbar = true;
      }
      this.isLoading = false;
    },

    handleCancel() {
      this.dialogVisible = false;
    },
  },
});
</script>
